// frontend/src/features/categories/CategoryList.js
import { useGetAllCategoriesAdminQuery } from "./categoriesApiSlice"; // ✅ Updated import for admin query
import Category from "./Category";
import useAuth from "../../hooks/useAuth";
import useTitle from "../../hooks/useTitle";
import { Spinner, Alert, Pagination, Form, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
// ✅ Import actions from the new categorySlice
import { setPageNumber, setSearchQuery, resetCategoryListState } from './categorySlice'; 

const CategoryList = () => {
    useTitle('Nirakar Satguru: Categories List');

    const { id, isManager, isAdmin } = useAuth(); // Assuming useAuth provides user role info
    
    const dispatch = useDispatch();
    // Get pagination and search states from Redux store
    const { pageNumber, searchQuery, isActiveFilter } = useSelector((state) => state.category); 

    // Local state for isActive filter, managed by UI, then dispatched to Redux
    const [localIsActiveFilter, setLocalIsActiveFilter] = useState(isActiveFilter || ''); 
    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || '');

    // Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            // Only dispatch if the local search query has changed from the Redux state
            if (localSearchQuery !== searchQuery) {
                dispatch(setSearchQuery(localSearchQuery));
                dispatch(setPageNumber(1)); // Reset to first page on new search
            }
        }, 500); // 500ms debounce

        return () => {
            clearTimeout(handler);
        };
    }, [localSearchQuery, searchQuery, dispatch]);

    // Handle isActive filter change
    useEffect(() => {
        if (localIsActiveFilter !== isActiveFilter) {
            dispatch(setPageNumber(1)); // Reset to first page on filter change
            // Dispatching to Redux store if you need to persist isActive filter
            // Note: categorySlice.js should have an action for setIsActiveFilter if you want to dispatch it
            // For now, it's passed directly to the query
        }
    }, [localIsActiveFilter, isActiveFilter, dispatch]);


    const {
        data: categoriesData,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetAllCategoriesAdminQuery({ page: pageNumber, search: searchQuery, isActive: localIsActiveFilter }, {
        pollingInterval: 15000, // Poll every 15 seconds
        refetchOnMountOrArgChange: true, // Refetch when component mounts or query args change
        skip: false, // Don't skip this query
    });

    // Reset pagination and search state on component unmount (optional cleanup)
    useEffect(() => {
        return () => {
            dispatch(resetCategoryListState());
        };
    }, [dispatch]);

    const handlePageChange = (newPage) => {
        dispatch(setPageNumber(newPage));
    };

    const handleSearchChange = (e) => {
        setLocalSearchQuery(e.target.value);
    };

    const handleIsActiveFilterChange = (e) => {
        setLocalIsActiveFilter(e.target.value);
    };

    let content;
    let totalPages = categoriesData?.totalPages || 1;
    let currentPage = categoriesData?.currentPage || 1;
    let filteredCategoriesCount = categoriesData?.filteredCategoriesCount || 0;
    let filteredCategoryIds = categoriesData?.ids || []; // Get IDs from RTK Query normalized data

    console.log("CategoryList: Query State - ", { isLoading, isSuccess, isError, error, data: categoriesData }); // ✅ Added for debugging
    console.log("CategoryList: IDs extracted from categoriesData:", filteredCategoryIds); // ✅ Added for debugging
    console.log("CategoryList.js: I am rendering after changes!"); // ✅ Added for testing

    if (isLoading) {
        content = <Spinner animation="border" role="status" className="d-block mx-auto mt-5" />;
    } else if (isError) {
        content = <Alert variant="danger" className="text-center mt-5">Error: {error?.data?.message || 'Failed to load categories'}</Alert>;
    } else if (isSuccess && categoriesData) {
        content = (
            <>
                <p>TESTING CATEGORY LIST RENDER</p> {/* ✅ Added for testing */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2 className="mb-0">Categories List ({filteredCategoriesCount})</h2>
                    <Link to="/dashboard/categories/new" className="btn btn-primary">Add New Category</Link>
                </div>

                <Form className="mb-4">
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="searchQuery">
                                <Form.Label>Search Categories</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by name or description"
                                    value={localSearchQuery}
                                    onChange={handleSearchChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="isActiveFilter">
                                <Form.Label>Filter by Status</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={localIsActiveFilter}
                                    onChange={handleIsActiveFilterChange}
                                >
                                    <option value="">All</option>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>

                <div className="table-responsive">
                    <table className="table table-bordered table-hover">
                        <thead className="table-light">
                            <tr>
                                <th scope="col">#</th>
                                <th scope="col">Name</th>
                                {/* <th scope="col">Slug</th> */}
                                <th scope="col">Description</th>
                                <th scope="col">Image</th>
                                <th scope="col">Order</th>
                                <th scope="col">Active</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCategoryIds.map((categoryId, index) => (
                                <Category key={categoryId} categoryId={categoryId} index={index} />
                            ))}
                        </tbody>
                    </table>
                </div>
                
                {!isLoading && !isError && filteredCategoryIds.length === 0 && (
                    <p className="text-center mt-4">No categories found.</p>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <Pagination className="justify-content-center mt-4">
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                        {[...Array(totalPages)].map((_, i) => (
                            <Pagination.Item
                                key={i + 1}
                                active={i + 1 === currentPage}
                                onClick={() => handlePageChange(i + 1)}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                )}
            </>
        );
    }

    return content;
};
export default CategoryList;